import type { PiiType, TextDetection } from './types';

/**
 * Form-field detector: when a document has labeled fields like
 * "Tenant Name: John Smith" or "DOB: 01/15/1985", the LABEL is a stronger
 * signal about the type than NER guessing. We capture the value after the
 * label and tag it with the type implied by the label.
 *
 * Recall on lease agreements / tax forms / applications goes up
 * dramatically with this layer because:
 *  - All-caps names ("SMITH, JOHN") that NER misses still get caught
 *  - Multi-line addresses get the first line at minimum
 *  - Date-of-birth rows are correctly typed as `dob` not `date`
 */

type FieldRule = {
  type: PiiType;
  /** Label patterns. Case-insensitive. Word boundaries enforced. */
  labels: string[];
  /** Confidence for the captured value. */
  confidence: number;
  /** Optional shape check on the captured value — reject the match if
   *  this returns false. Lets us require digits in a `(Phone) ___`
   *  capture, 5-digits in a `(Zip)` capture, etc. Free-form types
   *  (name, address) skip validation. */
  valueValidator?: (value: string) => boolean;
};

const digits = (v: string): string => v.replace(/\D/g, '');

const isPhoneLike = (v: string): boolean => {
  const d = digits(v);
  return d.length >= 7 && d.length <= 15;
};
const isZipLike = (v: string): boolean => /^\d{5}(?:-\d{4})?$/.test(v.trim());
const isSsnLike = (v: string): boolean => /\d{3}\D?\d{2}\D?\d{4}/.test(v);
const isEmailLike = (v: string): boolean => v.includes('@');
const containsDigit = (v: string): boolean => /\d/.test(v);
const isIdLike = (v: string): boolean => {
  // Drivers-license / passport / state-ID values are alphanumeric
  // (often with hyphens). Require at least 4 alphanum chars and
  // at least one digit so we don't capture pure-letter junk.
  const stripped = v.replace(/\W/g, '');
  return stripped.length >= 4 && /\d/.test(stripped);
};

const RULES: FieldRule[] = [
  {
    type: 'name',
    labels: [
      'name',
      'full name',
      'first name',
      'last name',
      'middle name',
      'tenant',
      'tenant name',
      "tenant's name",
      'landlord',
      'landlord name',
      "landlord's name",
      'lessor',
      'lessee',
      'applicant',
      'applicant name',
      'customer',
      'customer name',
      'patient',
      'patient name',
      'employee',
      'employee name',
      'guarantor',
      "guarantor's name",
      'co-signer',
      'cosigner',
      'witness',
      'beneficiary',
      'spouse',
      'borrower',
      "borrower's name",
      'signed by',
      'authorized by',
      'printed name',
      'print name',
      // Signature-block patterns common in legal docs.
      'by',
      "owner's name",
      'agent',
      "agent's name",
      'representative',
      'attorney',
      "attorney's name",
      'signatory',
    ],
    confidence: 0.9,
  },
  {
    type: 'dob',
    labels: [
      'dob',
      'd.o.b.',
      'date of birth',
      'birth date',
      'birthday',
      'born',
      'born on',
    ],
    confidence: 0.95,
    valueValidator: containsDigit,
  },
  {
    type: 'date',
    labels: [
      'date',
      'signed date',
      'signature date',
      'effective date',
      'lease date',
      'start date',
      'end date',
      'expiration date',
      'expiry date',
      'issue date',
      'issued',
      'lease begins',
      'lease ends',
      'occupancy date',
      'move-in date',
      'move-out date',
    ],
    confidence: 0.85,
    valueValidator: containsDigit,
  },
  {
    type: 'address',
    labels: [
      'address',
      'mailing address',
      'home address',
      'street address',
      'residence',
      'tenant address',
      'property address',
      'premises',
      'premises address',
      'billing address',
      'shipping address',
      'forwarding address',
      'previous address',
    ],
    confidence: 0.9,
  },
  {
    type: 'us_zip',
    labels: ['zip', 'zipcode', 'zip code', 'postal code', 'postcode', 'postal'],
    confidence: 0.9,
    valueValidator: isZipLike,
  },
  {
    type: 'phone',
    labels: [
      'phone',
      'phone number',
      'telephone',
      'tel',
      'cell',
      'cell phone',
      'mobile',
      'mobile phone',
      'home phone',
      'work phone',
      'fax',
    ],
    confidence: 0.9,
    valueValidator: isPhoneLike,
  },
  {
    type: 'email',
    labels: ['email', 'e-mail', 'email address', 'e-mail address'],
    confidence: 0.95,
    valueValidator: isEmailLike,
  },
  {
    type: 'ssn',
    labels: ['ssn', 's.s.n.', 'social security', 'social security number', 'social security #'],
    confidence: 0.95,
    valueValidator: isSsnLike,
  },
  {
    type: 'drivers_license',
    labels: [
      "driver's license",
      'drivers license',
      'driver license',
      'driver id',
      "driver's license number",
      "driver's license #",
      "driver's license no",
      "driver's license no.",
      'license number',
      'license no',
      'license no.',
      'license #',
      'license id',
      'state license',
      'state id',
      'state id number',
      'state-issued id',
      'identification number',
      'identification no',
      'identification no.',
      'identification #',
      // Common abbreviations printed on US licenses.
      'dl',
      'dl number',
      'dl no',
      'dl no.',
      'dl #',
      'dl#',
      'dln',
      'lic',
      'lic.',
      'lic #',
      'lic no',
      'lic no.',
    ],
    confidence: 0.9,
    valueValidator: isIdLike,
  },
  {
    type: 'passport',
    labels: [
      'passport',
      'passport number',
      'passport no',
      'passport no.',
      'passport #',
      'passport id',
      'travel document number',
      'travel document no',
      'travel doc no',
    ],
    confidence: 0.9,
    valueValidator: isIdLike,
  },
  {
    type: 'bank_account',
    labels: [
      'account number',
      'account no',
      'account no.',
      'account #',
      'acct number',
      'acct no',
      'acct no.',
      'acct #',
      'bank account',
      'bank account number',
      'checking account',
      'checking account number',
      'savings account',
      'savings account number',
      'iban',
      'sort code',
    ],
    confidence: 0.9,
    valueValidator: isIdLike,
  },
  {
    type: 'routing_number',
    labels: [
      'routing number',
      'routing no',
      'routing no.',
      'routing #',
      'aba',
      'aba number',
      'aba routing number',
      'rtn',
    ],
    confidence: 0.95,
    valueValidator: isIdLike,
  },
  {
    type: 'vin',
    labels: ['vin', 'vehicle identification number', 'vin number', 'vin #'],
    confidence: 0.9,
    valueValidator: isIdLike,
  },
  {
    type: 'license_plate',
    labels: [
      'license plate',
      'plate',
      'plate number',
      'plate no',
      'plate no.',
      'plate #',
      'tag',
      'tag number',
      'tag #',
    ],
    confidence: 0.9,
    valueValidator: isIdLike,
  },
];

const escapeRe = (s: string): string => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Build a single regex per rule that matches `<label> <separator> <value>`
 * up to a line break (or another label-looking thing).
 *
 * Two recognised label shapes:
 *  - Bare: `Label: value` / `Label= value` / `Label\tvalue`. Requires
 *    an explicit separator so we don't false-match "Name John" inside
 *    arbitrary prose.
 *  - Wrapped: `(Label) value` / `[Label] value`. Common in OCR'd US
 *    fillable forms where the field name is bracketed and the value
 *    is whitespace-separated. The brackets themselves act as a strong
 *    enough boundary that we accept any whitespace as separator.
 */
const buildRulePattern = (labels: readonly string[]): RegExp => {
  // Sort by length descending so "tenant name" matches before "tenant".
  const sorted = [...labels].sort((a, b) => b.length - a.length);
  const labelGroup = sorted.map(escapeRe).join('|');
  return new RegExp(
    `(?:^|\\n)\\s*(?:` +
      `[\\(\\[](?:${labelGroup})[\\)\\]]\\s+` +
      `|` +
      `(?:${labelGroup})\\s*(?:[:=\\u2013\\u2014]|\\t)\\s*` +
    // Value runs up to: end-of-line, end-of-string, semicolon, 4+
    // space gap (column separator in fixed-width forms), OR the
    // start of another bracketed label (multi-field lines like
    // "(Phone Number) ___ at (Street or Address) ___" should split
    // at the second "(").
    `)([^\\n;]{2,200}?)(?=\\s*(?:\\n|$|;|\\s{4,}|[\\(\\[]\\w))`,
    'gi',
  );
};

/** All label keywords across every rule — used to reject values that are
 *  themselves labels (a common false positive in multi-column forms). */
const ALL_LABELS_LOWER: ReadonlySet<string> = new Set(
  RULES.flatMap((r) => r.labels.map((l) => l.toLowerCase())),
);

/** Heading / boilerplate stop words. If a `name` candidate contains any of
 *  these, it's almost certainly a section heading, not a person. */
const NAME_STOP_WORDS: ReadonlySet<string> = new Set([
  'section',
  'governs',
  'applicable',
  'subject',
  'lease',
  'agreement',
  'insurance',
  'landlord',
  'tenant',
  'lessor',
  'lessee',
  'pursuant',
  'whereas',
  'hereby',
  'herein',
  'hereinafter',
  'shall',
  'must',
  'will',
  'may',
  'provide',
  'receive',
  'sign',
  'signed',
  'signature',
  'copy',
  'rent',
  'security',
  'deposit',
  'utilities',
  'maintenance',
  'damages',
  'termination',
  'eviction',
  'notice',
  'law',
  'legal',
  'court',
  'jurisdiction',
  'binding',
  'represents',
  'warrants',
]);

/** Common English function words — if a candidate phrase contains any of
 *  these, it's a sentence/heading rather than a name. Lower-cased. */
const SENTENCE_FUNCTION_WORDS: ReadonlySet<string> = new Set([
  'the',
  'a',
  'an',
  'of',
  'to',
  'and',
  'or',
  'in',
  'on',
  'at',
  'for',
  'with',
  'by',
  'from',
  'is',
  'are',
  'was',
  'were',
  'be',
  'been',
  'being',
  'has',
  'have',
  'had',
  'do',
  'does',
  'did',
  'this',
  'that',
  'these',
  'those',
  'as',
  'if',
  'but',
  'not',
  'no',
  'all',
  'any',
  'each',
  'every',
  'such',
  'so',
]);

const stripPunct = (s: string): string => s.toLowerCase().replace(/[.,;:!?]+$/, '');

const isLikelyHeading = (value: string, type: PiiType): boolean => {
  const trimmed = value.trim();
  // Looks like another label: "Address:" or "Phone Number:"
  if (trimmed.endsWith(':')) return true;
  const lower = trimmed.toLowerCase();
  // Exact match (or label + colon) against a known label keyword.
  if (ALL_LABELS_LOWER.has(lower) || ALL_LABELS_LOWER.has(lower.replace(/:$/, ''))) {
    return true;
  }
  if (type === 'name') {
    const words = trimmed.split(/\s+/);
    // Truly long values (>6 words) are sentences, not names.
    if (words.length > 6) return true;
    // Candidate is a sentence/heading if it contains an English function
    // word ("the", "of", "to", "shall"...) — pure names don't. This
    // distinguishes "JOHN MICHAEL DOE" (allowed) from "SUBJECT TO LAW"
    // (rejected) regardless of casing.
    for (const w of words) {
      if (SENTENCE_FUNCTION_WORDS.has(stripPunct(w))) return true;
      if (NAME_STOP_WORDS.has(stripPunct(w))) return true;
    }
  }
  return false;
};

/**
 * Find labeled fields and return one detection per labeled value.
 */
export const detectFormFields = (text: string): TextDetection[] => {
  const out: TextDetection[] = [];
  for (const rule of RULES) {
    const re = buildRulePattern(rule.labels);
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      const valueText = m[1]?.trim();
      if (!valueText || valueText.length < 2) continue;
      if (isLikelyHeading(valueText, rule.type)) continue;
      if (rule.valueValidator && !rule.valueValidator(valueText)) continue;
      const valueOffset = m[0].lastIndexOf(valueText);
      if (valueOffset < 0) continue;
      const start = m.index + valueOffset;
      const end = start + valueText.length;
      out.push({
        type: rule.type,
        text: valueText,
        start,
        end,
        confidence: rule.confidence,
        source: 'form',
      });
    }
  }
  return out;
};
